import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Lock, CheckCircle, AlertCircle } from 'lucide-react';

export default function AcceptInvitePage() {
    const { token }   = useParams();
    const navigate    = useNavigate();
    const [status, setStatus]     = useState('loading'); // loading | valid | invalid | done
    const [userData, setUserData] = useState(null);
    const [password, setPassword] = useState('');
    const [confirm, setConfirm]   = useState('');
    const [error, setError]       = useState('');
    const [saving, setSaving]     = useState(false);

    useEffect(() => {
        fetch(`/api/invite/validate/${token}`)
            .then(r => r.json())
            .then(d => {
                if (d.valid) { setUserData(d); setStatus('valid'); }
                else         { setStatus('invalid'); setError(d.message); }
            })
            .catch(() => setStatus('invalid'));
    }, [token]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        if (password.length < 10) { setError('Password must be at least 10 characters.'); return; }
        if (password !== confirm)  { setError('Passwords do not match.'); return; }

        setSaving(true);
        try {
            const res  = await fetch(`/api/invite/accept/${token}`, {
                method:  'POST',
                headers: { 'Content-Type': 'application/json' },
                body:    JSON.stringify({ password }),
            });
            const data = await res.json();
            if (res.ok) { setStatus('done'); }
            else        { setError(data.message || 'Activation failed.'); }
        } catch {
            setError('Network error — please try again.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="app" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', background: 'var(--off-white)' }}>
            <div style={{ width: '100%', maxWidth: '440px', padding: '1rem' }}>
                <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
                    <div className="trf-header-logo-text" style={{ background: 'none', padding: 0, fontSize: '1.6rem', color: 'var(--navy)' }}>
                        <span style={{ color: 'var(--navy)' }}>Titanium</span>{' '}
                        <span style={{ color: 'var(--teal)' }}>Surgical Arts</span>
                    </div>
                    <p style={{ color: 'var(--text-muted)', marginTop: '0.4rem', fontSize: '0.85rem', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                        Staff Account Activation
                    </p>
                </div>

                <div className="trf-card">
                    {status === 'loading' && (
                        <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-muted)' }}>
                            <span className="trf-spinner" style={{ width: '2rem', height: '2rem', display: 'inline-block' }} />
                            <p style={{ marginTop: '1rem' }}>Validating your invite link…</p>
                        </div>
                    )}

                    {status === 'invalid' && (
                        <div style={{ textAlign: 'center', padding: '1.5rem' }}>
                            <AlertCircle size={48} style={{ color: '#dc2626', marginBottom: '1rem' }} />
                            <p className="trf-modal-title">Link Expired or Invalid</p>
                            <p className="trf-modal-message">{error || 'This invite link is no longer valid. Please ask your administrator to send a new invite.'}</p>
                            <a href="/login" style={{ color: 'var(--teal)', fontWeight: 700 }}>← Go to Login</a>
                        </div>
                    )}

                    {status === 'done' && (
                        <div style={{ textAlign: 'center', padding: '1.5rem' }}>
                            <CheckCircle size={48} style={{ color: 'var(--teal)', marginBottom: '1rem' }} />
                            <p className="trf-modal-title">Account Activated!</p>
                            <p className="trf-modal-message">Your account is ready. You can now sign in to the staff portal.</p>
                            <button className="trf-btn-submit" onClick={() => navigate('/login')}>Go to Login</button>
                        </div>
                    )}

                    {status === 'valid' && (
                        <>
                            <h2 className="trf-section-title" style={{ borderBottom: 'none', marginBottom: '0.25rem', fontSize: '0.8rem' }}>
                                <Lock size={13} /> Set Your Password
                            </h2>
                            <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem', marginBottom: '1.5rem' }}>
                                Welcome, <strong>{userData?.name}</strong>! You're joining as <strong style={{ color: 'var(--teal)', textTransform: 'capitalize' }}>{userData?.role}</strong>.<br />
                                Choose a strong password (minimum 10 characters).
                            </p>
                            <form onSubmit={handleSubmit}>
                                <div style={{ marginBottom: '1rem' }}>
                                    <label className="trf-label">New Password</label>
                                    <input type="password" className="trf-input" placeholder="At least 10 characters"
                                        value={password} onChange={e => setPassword(e.target.value)} required autoComplete="new-password" />
                                </div>
                                <div style={{ marginBottom: '1.5rem' }}>
                                    <label className="trf-label">Confirm Password</label>
                                    <input type="password" className="trf-input" placeholder="Repeat password"
                                        value={confirm} onChange={e => setConfirm(e.target.value)} required autoComplete="new-password" />
                                </div>
                                {error && (
                                    <div style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626', borderRadius: '6px', padding: '0.65rem 0.9rem', fontSize: '0.85rem', marginBottom: '1rem' }}>
                                        {error}
                                    </div>
                                )}
                                <button type="submit" className="trf-btn-submit" disabled={saving}>
                                    {saving ? <><span className="trf-spinner" /> Activating…</> : 'Activate Account'}
                                </button>
                            </form>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
